import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconDashboard } from '../../../../../components/app/icons/dashboard';
import { IconInvestment } from '../../../../../components/app/icons/investment';
import { IconProfits } from '../../../../../components/app/icons/profits';
import { IconDividends } from '../../../../../components/app/icons/dividends';
import { IconInvisible } from '../../../../../components/app/icons/invisible';
import { IconWallet } from '../../../../../components/app/icons/wallet';
import styles from './hero-mobile-application.module.css';

const PhoneClock: React.FC = () => {
    const [time, setTime] = useState('');

    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            setTime(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`);
        };

        updateTime();
        const interval = window.setInterval(updateTime, 1000);
        return () => window.clearInterval(interval);
    }, []);

    return (
        <text x="27" y="31" fontSize="7" fontWeight="700">
            {time}
        </text>
    );
};

/** Drawn from the native iOS overview, with the same illustrative values as the web preview. */
export const HeroMobileApplication: React.FC = () => {
    const { t } = useTranslation();
    const copy = (key: string) => t(`pages.homepage.heroApp.${key}`);
    const metrics = [
        { x: 20, y: 174, label: 'realized', amount: '84 520 Kč', color: '#25a68d' },
        { x: 116, y: 174, label: 'unrealized', amount: '137 280 Kč', color: '#25a68d' },
        { x: 20, y: 220, label: 'total', amount: '1 358 780 Kč', color: '#292450' },
        { x: 116, y: 220, label: 'dividends', amount: '28 460 Kč', color: '#292450' },
        { x: 20, y: 260, label: 'invested', amount: '1 108 520 Kč', color: '#292450' },
        { x: 20, y: 301, label: 'currencyImpact', amount: '12 049 Kč', color: '#25a68d' },
        { x: 116, y: 301, label: 'fees', amount: '1 365 Kč', color: '#e27991' },
    ];
    return (
        <g className={styles.app}>
            <defs>
                <clipPath id="hero-phone-screen">
                    <rect x="8" y="8" width="204" height="424" rx="33" />
                </clipPath>
                <linearGradient id="hero-phone-nav" x1="0" x2="1" y1="0" y2="1">
                    <stop stopColor="#27b5fb" />
                    <stop offset="1" stopColor="#198cf5" />
                </linearGradient>
            </defs>
            <g clipPath="url(#hero-phone-screen)">
                <rect className={styles.surface} x="8" y="8" width="204" height="424" rx="33" />
                <g className={styles.header}>
                    <g fill="#b9aea6" color="#b9aea6">
                        <PhoneClock />
                        <path d="M169 27h2v4h-2zm4-2h2v6h-2zm4-2h2v8h-2z" fill="currentColor" />
                        <rect
                            x="184"
                            y="25"
                            width="12"
                            height="6"
                            rx="2"
                            stroke="currentColor"
                            strokeWidth="0.8"
                            fill="none"
                        />
                        <rect x="186" y="26.5" width="8" height="3" rx="0.6" fill="currentColor" />
                    </g>
                    <circle cx="29" cy="57" r="9" fill="#b8e8fc" />
                    <text x="29" y="59.5" textAnchor="middle" fontSize="6" fill="#329bd6">
                        JD
                    </text>
                    <circle cx="52" cy="57" r="9" fill="#eeedf5" />
                    <IconInvisible svgOnly x="46" y="51" width="12" height="12" className={styles.appIcon} />
                    <text x="175" y="60" textAnchor="end" fontSize="9" fontWeight="700" fill="#2289f6">
                        {copy('summary')}
                    </text>
                    <circle cx="191" cy="57" r="9" fill="#2289f6" />
                    <IconWallet svgOnly x="186.5" y="52.5" width="9" height="9" className={styles.navIcon} />
                    <text x="20" y="100" fontSize="15" fontWeight="700">
                        {copy('overview')}
                    </text>
                </g>
                <g className={styles.tabs} transform="translate(0 14)">
                    <text x="20" y="104" fontSize="8" fontWeight="700">
                        {copy('stocks')}
                    </text>
                    <text x="49" y="104" fontSize="7.8" fill="#888398">
                        {copy('personalFinance')}
                    </text>
                    <text x="116" y="104" fontSize="7.8" fill="#888398">
                        Investown
                    </text>
                    <text x="161" y="104" fontSize="7.8" fill="#888398">
                        {copy('all')}
                    </text>
                    <path d="M20 109H29" stroke="#39b4ef" />
                </g>
                <g className={styles.primaryValue} transform="translate(0 28)">
                    <text x="20" y="133" className={styles.label} fill="#2289f6">
                        {copy('portfolioValue')}
                    </text>
                    <text x="20" y="158" fontSize="18" fontWeight="900" fill="#2289f6">
                        1 245 800 Kč
                    </text>
                </g>
                {metrics.map(({ x, y, label, amount, color }, index) => (
                    <g
                        key={label}
                        className={styles.metric}
                        transform="translate(0 28)"
                        style={{ animationDelay: `calc(${6.4 + index * 0.14}s * var(--hero-motion-scale))` }}
                    >
                        <text x={x} y={y + 8} className={styles.label}>
                            {copy(label)}
                        </text>
                        <text x={x} y={y + 24} fontSize={label === 'invested' ? 11 : 9.5} fontWeight="900" fill={color}>
                            {amount}
                        </text>
                    </g>
                ))}
                <rect className={styles.navOutline} x="42" y="382" width="138" height="30" rx="15" pathLength="1" />
                <g className={styles.nav}>
                    <rect x="42" y="382" width="138" height="30" rx="15" fill="url(#hero-phone-nav)" />
                    <rect x="49" y="387" width="55" height="20" rx="10" fill="white" fillOpacity="0.18" />
                    <IconDashboard svgOnly x="55" y="392" width="9" height="9" className={styles.navIcon} />
                    <text x="81" y="399.5" textAnchor="middle" fill="white" fontSize="7" fontWeight="700">
                        {copy('overview')}
                    </text>
                    <IconInvestment svgOnly x="113" y="392" width="9" height="9" className={styles.navIcon} />
                    <IconProfits svgOnly x="136" y="392" width="9" height="9" className={styles.navIcon} />
                    <IconDividends svgOnly x="159" y="392" width="9" height="9" className={styles.navIcon} />
                </g>
            </g>
        </g>
    );
};
